import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function StudentLifeEventsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    events: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadSection();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load section');
      
      const result = await response.json();
      if (!result.ok || !Array.isArray(result.data)) {
        throw new Error('Invalid section data');
      }

      const section = result.data.find(s => s.section_type === 'student-life-events');
      if (!section) throw new Error('Events section not found');

      setSectionId(section.id);

      // Parse content_published
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      
      const enData = published?.en || formData;
      setFormData({
        kicker: enData.kicker || '',
        title: enData.title || '',
        description: enData.description || '',
        events: Array.isArray(enData.events) ? enData.events : []
      });

      // Load pending changes
      let pendingFieldsMap = {};
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${section.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data?.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingFieldsMap[change.field_id] = true;
              }
            });
          }
        }
      }
      
      setPendingFields(pendingFieldsMap);
    } catch (err) {
      console.error('Load section error:', err);
      toast?.error?.('Failed to load events section');
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (index, field, value) => {
    const newEvents = [...formData.events];
    newEvents[index] = { ...newEvents[index], [field]: value };
    setFormData({ ...formData, events: newEvents });
  };

  const handleEventImageUpload = async (index, file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast?.error?.('Only JPG, PNG, WebP allowed');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast?.error?.('Image must be under 5MB');
      return;
    }

    const uploadKey = `event_${index}_image`;
    setImageUploading(prev => ({ ...prev, [uploadKey]: true }));
    setImageProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setImageProgress(prev => ({ ...prev, [uploadKey]: percentComplete }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const result = JSON.parse(xhr.responseText);
          if (result.ok && result.data && result.data.media_id) {
            const newEvents = [...formData.events];
            if (!Array.isArray(newEvents[index].images)) {
              newEvents[index].images = [];
            }
            newEvents[index].images.push(result.data.media_id);
            setFormData({ ...formData, events: newEvents });
            toast?.success?.('Image uploaded');
          } else {
            toast?.error?.('Upload failed');
          }
        }
      });

      xhr.addEventListener('error', () => {
        toast?.error?.('Upload error');
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataObj);
    } catch (err) {
      console.error('Upload error:', err);
      toast?.error?.('Upload failed');
    } finally {
      setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const submitField = async (fieldId, value) => {
    if (!sectionId || submitting[fieldId]) return;
    
    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const response = await fetch(
        `${apiBase}/cms/section/${sectionId}/field`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            field_id: fieldId,
            value: value,
            locale: 'en'
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        toast?.success?.('Change submitted for approval');
      } else {
        toast?.error?.(result.message || 'Failed to submit');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast?.error?.('Submission failed');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (loading) {
    return <div className="form-loading">Loading events section...</div>;
  }

  return (
    <div className="admin-form">
      <ToastContainer />
      
      <h2>Events & Competitions</h2>

      <div className="form-group">
        <label>Kicker</label>
        <input
          type="text"
          value={formData.kicker}
          onChange={(e) => setFormData({ ...formData, kicker: e.target.value })}
          placeholder="e.g., Events & Competitions"
        />
        <button 
          onClick={() => submitField('kicker', formData.kicker)}
          disabled={submitting.kicker}
          className={pendingFields.kicker ? 'pending' : ''}
        >
          {submitting.kicker ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <button 
          onClick={() => submitField('title', formData.title)}
          disabled={submitting.title}
          className={pendingFields.title ? 'pending' : ''}
        >
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
        />
        <button 
          onClick={() => submitField('description', formData.description)}
          disabled={submitting.description}
          className={pendingFields.description ? 'pending' : ''}
        >
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <h3>Events</h3>
      {formData.events.map((event, index) => (
        <div key={index} className="nested-item-card">
          <h4>Event {index + 1}</h4>
          
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={event.title || ''}
              onChange={(e) => handleEventChange(index, 'title', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={event.subtitle || ''}
              onChange={(e) => handleEventChange(index, 'subtitle', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={event.description || ''}
              onChange={(e) => handleEventChange(index, 'description', e.target.value)}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Highlights (one per line)</label>
            <textarea
              value={Array.isArray(event.highlights) ? event.highlights.join('\n') : ''}
              onChange={(e) => handleEventChange(index, 'highlights', e.target.value.split('\n').filter(h => h.trim()))}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Schedule (one per line)</label>
            <textarea
              value={Array.isArray(event.schedule) ? event.schedule.join('\n') : ''}
              onChange={(e) => handleEventChange(index, 'schedule', e.target.value.split('\n').filter(s => s.trim()))}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Images</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleEventImageUpload(index, e.target.files?.[0])}
              disabled={imageUploading[`event_${index}_image`]}
            />
            {imageUploading[`event_${index}_image`] && (
              <div className="progress-bar">
                <div style={{ width: `${imageProgress[`event_${index}_image`]}%` }} />
              </div>
            )}
          </div>

          <button 
            onClick={() => submitField(`events.${index}`, formData.events[index])}
            disabled={submitting[`events.${index}`]}
            className={pendingFields[`events.${index}`] ? 'pending' : ''}
          >
            Submit Event
          </button>
        </div>
      ))}
    </div>
  );
}
